import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  FileText,
  Trash2,
  Eye,
  Edit,
  Plus,
  Search,
  AlertCircle,
  X,
  Clock,
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";

function InstructionManagement() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [instructions, setInstructions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewContent, setPreviewContent] = useState("");
  const [showPreview, setShowPreview] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadData, setUploadData] = useState({ identifier: "", content: "" });
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [mode, setMode] = useState("create");
  const [loadingEditor, setLoadingEditor] = useState(false);

  useEffect(() => {
    fetchInstructions();
  }, []);

  const fetchInstructions = async () => {
    try {
      const { data } = await axios.get("/api/prompt-admin/list");
      setInstructions(data.files || []);
    } catch (error) {
      console.error("Failed to fetch instructions:", error);
      setError("Failed to load instructions");
    } finally {
      setLoading(false);
    }
  };

  const handlePreview = async (identifier) => {
    try {
      const { data } = await axios.get(`/api/prompt-admin/get/${identifier}`);
      setPreviewContent(data.content);
      setSelectedFile(identifier);
      setShowPreview(true);
    } catch (error) {
      console.error("Failed to preview instruction:", error);
      setError(
        error.response?.data?.message || "Failed to load instruction preview",
      );
    }
  };

  const openCreateModal = () => {
    setMode("create");
    setUploadData({ identifier: "", content: "" });
    setShowUploadModal(true);
  };

  const openEditModal = async (identifier) => {
    setMode("edit");
    setUploadData({ identifier, content: "" });
    setShowUploadModal(true);
    setLoadingEditor(true);

    try {
      const { data } = await axios.get(`/api/prompt-admin/get/${identifier}`);
      setUploadData({ identifier, content: data.content || "" });
    } catch (error) {
      console.error("Failed to load instruction for edit:", error);
      setError(error.response?.data?.message || "Failed to load instruction");
      setShowUploadModal(false);
    } finally {
      setLoadingEditor(false);
    }
  };

  const handleDelete = async (identifier) => {
    if (!confirm(`Are you sure you want to delete "${identifier}"?`)) {
      return;
    }

    try {
      await axios.delete(`/api/prompt-admin/delete/${identifier}`);
      setInstructions((prev) =>
        prev.filter((file) => file.identifier !== identifier),
      );
      if (selectedFile === identifier) {
        setShowPreview(false);
        setSelectedFile(null);
      }
    } catch (error) {
      console.error("Failed to delete instruction:", error);
      setError(error.response?.data?.message || "Failed to delete instruction");
    }
  };

  const handleUpload = async () => {
    if (!uploadData.identifier || !uploadData.content) {
      setError("Identifier and content are required");
      return;
    }

    setUploading(true);
    setError("");

    try {
      await axios.post("/api/prompt-admin/update-with-preview", {
        identifier: uploadData.identifier,
        content: uploadData.content,
        confirmChanges: true,
      });

      await fetchInstructions();
      setShowUploadModal(false);
      setUploadData({ identifier: "", content: "" });
    } catch (error) {
      console.error("Upload failed:", error);
      setError(error.response?.data?.message || "Failed to save instruction");
    } finally {
      setUploading(false);
    }
  };

  const filteredInstructions = instructions.filter((file) =>
    file.identifier.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  if (!user) {
    navigate("/login");
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <button
                onClick={() => navigate("/dashboard")}
                className="flex items-center text-gray-600 hover:text-gray-900"
              >
                <FileText className="h-5 w-5 mr-2" />
                <span className="text-lg font-semibold">
                  Instruction Management
                </span>
              </button>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={openCreateModal}
                className="btn-primary flex items-center"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Instruction
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Error Alert */}
      {error && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4">
          <div className="p-4 bg-danger-50 border border-danger-200 rounded-md flex items-center">
            <AlertCircle className="h-5 w-5 text-danger-600 mr-2" />
            <span className="text-danger-800 text-sm">{error}</span>
            <button
              onClick={() => setError("")}
              className="ml-auto text-danger-600 hover:text-danger-800"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          {/* Search */}
          <div className="mb-6">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="Search instructions..."
                className="input-field pl-10"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          {/* Instructions List */}
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
              <p className="mt-4 text-gray-600">Loading instructions...</p>
            </div>
          ) : (
            <div className="card">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Identifier
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Size
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Last Updated
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredInstructions.map((file) => (
                      <tr
                        key={file.identifier}
                        className="hover:bg-gray-50 cursor-pointer"
                        onClick={() => handlePreview(file.identifier)}
                      >
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {file.identifier}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {file.size
                            ? `${(file.size / 1024).toFixed(1)} KB`
                            : "Unknown"}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {file.updated
                            ? new Date(file.updated).toLocaleString()
                            : "Unknown"}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex justify-end space-x-2">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handlePreview(file.identifier);
                              }}
                              className="text-primary-600 hover:text-primary-900"
                              title="Preview"
                            >
                              <Eye className="h-4 w-4" />
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                openEditModal(file.identifier);
                              }}
                              className="text-gray-600 hover:text-gray-900"
                              title="Edit"
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(
                                  `/audit?identifier=${encodeURIComponent(file.identifier)}`,
                                );
                              }}
                              className="text-yellow-600 hover:text-yellow-900"
                              title="View Audit History"
                            >
                              <Clock className="h-4 w-4" />
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDelete(file.identifier);
                              }}
                              className="text-danger-600 hover:text-danger-900"
                              title="Delete"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-[90%] h-[72.5%] shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                {mode === "edit" ? "Update" : "Add"} Instruction
              </h3>
              <button
                onClick={() => {
                  setShowUploadModal(false);
                  setUploadData({ identifier: "", content: "" });
                  setMode("create");
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Identifier
                  </label>
                  <input
                    type="text"
                    className="input-field mt-1"
                    placeholder="instruction-name"
                    value={uploadData.identifier}
                    onChange={(e) =>
                      setUploadData((prev) => ({
                        ...prev,
                        identifier: e.target.value,
                      }))
                    }
                    disabled={uploading || mode === "edit"}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Content
                  </label>
                  {loadingEditor ? (
                    <div className="text-center py-6">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600 mx-auto"></div>
                      <p className="mt-3 text-gray-600 text-sm">
                        Loading content...
                      </p>
                    </div>
                  ) : (
                    <textarea
                      rows={16}
                      className="input-field mt-1"
                      placeholder="Enter instruction content..."
                      value={uploadData.content}
                      onChange={(e) =>
                        setUploadData((prev) => ({
                          ...prev,
                          content: e.target.value,
                        }))
                      }
                      disabled={uploading}
                    />
                  )}
                </div>
                <div className="flex space-x-3">
                  <button
                    onClick={() => {
                      setShowUploadModal(false);
                      setUploadData({ identifier: "", content: "" });
                      setMode("create");
                    }}
                    className="btn-secondary"
                    disabled={uploading}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleUpload}
                    disabled={
                      uploading ||
                      loadingEditor ||
                      !uploadData.identifier ||
                      !uploadData.content
                    }
                    className="btn-primary flex items-center"
                  >
                    {uploading ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    ) : null}
                    {uploading
                      ? "Saving..."
                      : mode === "edit"
                        ? "Save Changes"
                        : "Create"}
                  </button>
                </div>
              </div>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {showPreview && (
        <div className="fixed inset-0 z-50 bg-gray-900/60">
          <div className="h-full w-full p-4 sm:p-6 flex items-center justify-center">
            <div className="card w-full max-w-6xl h-[90vh] overflow-hidden">
              <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-b border-gray-200 bg-white">
                <div>
                  <h3 className="text-lg sm:text-xl font-semibold text-gray-900">
                    Preview
                  </h3>
                  <p className="text-sm text-gray-600 mt-1 break-all">
                    {selectedFile}
                  </p>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => {
                      if (selectedFile) {
                        setShowPreview(false);
                        openEditModal(selectedFile);
                      }
                    }}
                    className="btn-primary flex items-center"
                  >
                    <Edit className="h-4 w-4 mr-2" />
                    Edit
                  </button>

                  <button
                    onClick={() => {
                      setShowPreview(false);
                      setSelectedFile(null);
                      setPreviewContent("");
                    }}
                    className="btn-secondary flex items-center"
                  >
                    <X className="h-4 w-4 mr-2" />
                    Close
                  </button>
                </div>
              </div>

              <div className="h-full bg-gray-50">
                <div className="h-full overflow-auto p-4 sm:p-6">
                  <pre className="text-sm leading-relaxed text-gray-900 whitespace-pre-wrap">
                    {previewContent}
                  </pre>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default InstructionManagement;
